/**
 * データ参照・レコード単位編集画面 (Lite版)
 * 設計: docs/design/v03-record-level-data-editing-detail.md §7-2
 *
 * Full版 (DataBrowsePage) と対になる。Lite版はこれまで「今この店舗×月に何が入っているか」を
 * 一覧する手段が無く、/entry/lite の入力欄からしか確認できなかった。
 *
 * 枠外調整 (manual_adjustments) は参照のみ。書込みは既存の /entry/lite が担当する
 * (実装の二重化を避けるため、バックエンドも /lite-manual-adjustments へ委譲している)。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Filter, Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import { MonthPicker } from '@/components/MonthPicker';
import { csvEscape as csvEscapeShared } from '@/lib/csv-export';
import { toast } from '@/stores/toast';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };
type MasterItem = { id: string; name: string };
type Row = Record<string, unknown> & { record_id: string; version: string };
type RecordsResponse = {
  source_type: string; node_id: string; month: string;
  count: number; total_count: number; is_editable: boolean; rows: Row[];
};

const PAGE_SIZE = 100;

const SOURCE_TYPES = [
  { value: 'monthly_pl', label: '月次PL（勘定科目別）', targetLabel: '勘定科目', editable: true },
  { value: 'monthly_supplier_costs', label: '月次 仕入先別コスト', targetLabel: '仕入先', editable: true },
  { value: 'manual_adjustments', label: '枠外調整', targetLabel: '調整種別', editable: false },
] as const;

const ADJUSTMENT_TYPE_LABEL: Record<string, string> = {
  profit_correction: '損益修正', inter_store_transfer: '店舗間付替', hq_burden: '本部負担', other: 'その他',
};

const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthsBackList = (n: number): string[] => {
  const list: string[] = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    list.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return list;
};

const jpMonth = (m: string) => {
  const [y, mo] = m.split('-');
  return y && mo ? `${y}年${Number(mo)}月` : m;
};

/** Full 版と同じ扱い: 取込へ戻せる往復用CSVのため formula injection の先頭クォート前置はしない
 *  (§8-67)。ローカル再定義を避け、共有ヘルパーを escapeFormulas=false で呼ぶ。 */
const csvEscape = (v: unknown) => csvEscapeShared(v, false);

export function LiteDataBrowsePage() {
  const qc = useQueryClient();
  const [sourceType, setSourceType] = useState<string>('monthly_pl');
  const [storeId, setStoreId] = useState('');
  const [month, setMonth] = useState(currentYearMonth());
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<{ mode: 'create' | 'edit'; row: Partial<Row> } | null>(null);
  const [form, setForm] = useState<{ target_id: string; amount: string }>({ target_id: '', amount: '' });
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('');
  const [page, setPage] = useState(0);

  const spec = SOURCE_TYPES.find((s) => s.value === sourceType)!;
  const isEditable = spec.editable;

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'], queryFn: () => apiFetch<Store[]>('/stores'),
  });
  useEffect(() => { if (!storeId && stores.length > 0) setStoreId(stores[0]!.store_id); }, [stores, storeId]);

  const { data: masters = [] } = useQuery<MasterItem[]>({
    queryKey: [sourceType === 'monthly_pl' ? 'account-categories' : 'suppliers-lite'],
    queryFn: () => apiFetch<MasterItem[]>(sourceType === 'monthly_pl' ? '/account-categories' : '/suppliers-lite'),
    enabled: isEditable,
  });

  // ⚠️ limit/offset を送らないと lite-data.ts の既定 100 件で打ち切られ、
  //    101 件目以降が閲覧も編集も削除もできなくなる (S-11 の Design レビューで検出)。
  //    Full 版と同じページャを必ず持たせること。
  const records = useQuery<RecordsResponse>({
    queryKey: ['lite-data-records', sourceType, storeId, month, q, page],
    queryFn: () => {
      const p = new URLSearchParams({ source_type: sourceType, node_id: storeId, month });
      if (q) p.append('q', q);
      p.append('limit', String(PAGE_SIZE));
      p.append('offset', String(page * PAGE_SIZE));
      return apiFetch<RecordsResponse>(`/lite-data/records?${p}`);
    },
    enabled: Boolean(storeId && month),
  });

  const rows = records.data?.rows ?? [];
  const total = records.data?.total_count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 絞込を変えたら選択を破棄する (Full版と同じ規約。黙って持ち越すと誤削除になる)
  const filterKey = `${sourceType}|${storeId}|${month}|${q}`;
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current === filterKey) return;
    prevFilterKey.current = filterKey;
    setPage(0);
    setSelected((prev) => {
      if (prev.size > 0) toast.info(`絞り込みを変更したため、選択していた ${prev.size} 件を解除しました`);
      return new Set();
    });
  }, [filterKey]);

  useEffect(() => {
    if (records.data) setLiveMessage(`${total.toLocaleString()} 件を表示しています`);
  }, [records.data, total]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lite-data-records'] });
    qc.invalidateQueries({ queryKey: ['lite-data-summary'] });
  };

  const storeLabel = stores.find((s) => s.store_id === storeId)?.store_name ?? '';

  const downloadCsv = async () => {
    const headers = sourceType === 'manual_adjustments'
      ? ['adjustment_type', 'amount', 'note', 'applied_to_kpi']
      : ['target_id', 'target_name', 'amount'];
    // ⚠️ 画面は 1ページ分しか持っていないため、CSV は export=1 で全件を取り直す。
    //    rows をそのまま書き出すと「件数表示は全件なのに中身は先頭ページだけ」になる。
    const p = new URLSearchParams({ source_type: sourceType, node_id: storeId, month, export: '1' });
    if (q) p.append('q', q);
    const all = await apiFetch<RecordsResponse>(`/lite-data/records?${p}`);
    const lines = [headers.join(',')];
    for (const r of all.rows) lines.push(headers.map((h) => csvEscape(r[h])).join(','));
    const blob = new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${sourceType}_${month}_${storeId}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openCreate = () => {
    setModalError(null);
    setForm({ target_id: '', amount: '' });
    setEditTarget({ mode: 'create', row: {} });
  };
  const openEdit = (r: Row) => {
    setModalError(null);
    setForm({ target_id: String(r.target_id ?? ''), amount: String(r.amount ?? '') });
    setEditTarget({ mode: 'edit', row: r });
  };

  const submit = async () => {
    if (!editTarget) return;
    setSaving(true); setModalError(null);
    try {
      if (editTarget.mode === 'create') {
        await apiFetch('/lite-data/records', {
          method: 'POST',
          body: JSON.stringify({
            source_type: sourceType, node_id: storeId, month,
            target_id: form.target_id, amount: Number(form.amount),
          }),
        });
        toast.success('データを追加しました');
      } else {
        await apiFetch(`/lite-data/records/${editTarget.row.record_id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            source_type: sourceType, version: editTarget.row.version,
            values: { target_id: form.target_id, amount: Number(form.amount) },
          }),
        });
        toast.success('データを修正しました');
      }
      setEditTarget(null);
      refresh();
    } catch (e) {
      setModalError(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await apiFetch(`/lite-data/records/${deleteTarget.record_id}?source_type=${sourceType}`, { method: 'DELETE' });
      toast.success('データを削除しました');
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    } finally { setSaving(false); }
  };

  const doBulkDelete = async () => {
    setSaving(true);
    try {
      const res = await apiFetch<{ requested: number; deleted: number }>('/lite-data/records/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ source_type: sourceType, record_ids: [...selected] }),
      });
      toast.success(`${res.deleted} 件を削除しました`);
      setSelected(new Set());
      setBulkConfirm(false);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    } finally { setSaving(false); }
  };

  const pageIds = rows.map((r) => r.record_id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someSelected = pageIds.some((id) => selected.has(id));
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const columns = sourceType === 'manual_adjustments'
    ? [
      { key: 'adjustment_type', label: '調整種別', numeric: false },
      { key: 'amount', label: '金額', numeric: true },
      { key: 'note', label: '備考', numeric: false },
      { key: 'applied_to_kpi', label: 'KPI反映', numeric: false },
    ]
    : [
      { key: 'target_name', label: spec.targetLabel, numeric: false },
      { key: 'amount', label: '金額', numeric: true },
    ];

  const cell = (key: string, r: Row): string => {
    const v = r[key];
    if (v === null || v === undefined || v === '') return '−';
    if (key === 'adjustment_type') return ADJUSTMENT_TYPE_LABEL[String(v)] ?? String(v);
    if (key === 'applied_to_kpi') return v ? '反映する' : '反映しない';
    if (key === 'amount') return Number(v).toLocaleString();
    return String(v);
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-h1">取込済みデータ</h1>
        <p className="text-sm text-text-muted mt-1">
          店舗・年月・種別で絞り込んで確認し、行ごとの追加・修正・削除ができます
        </p>
      </div>

      <span className="sr-only" role="status" aria-live="polite">{liveMessage}</span>

      <section className="border border-surface-line rounded-lg bg-white p-4 space-y-4">
        <h2 className="text-h3 flex items-center gap-2"><Filter size={16} aria-hidden /> 絞り込み</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">種別</span>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCE_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">店舗</span>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="店舗を選択" /></SelectTrigger>
              <SelectContent>
                {stores.map((s) => <SelectItem key={s.store_id} value={s.store_id}>{s.store_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">対象月</span>
            {/* 36ヶ月をプルダウンに並べると目的の月まで延々スクロールすることになるため、
                単月必須のこの画面では月入力欄にする (2026-08-17 実運用フィードバック)。
                2026-09-07: ネイティブ<input type="month">はSafari等でBackspace編集時に不完全な
                中間表示になるクセがあり、年送り+月グリッドのMonthPickerに置き換えた
                (docs/ui-guidelines.md D3、36ヶ月分の長いスクロールも発生しない) */}
            <MonthPicker
              aria-label="対象月"
              value={month} onChange={setMonth}
              className="w-full min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring"
            />
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">検索</span>
            <div className="flex gap-2">
              <input type="search" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setQ(searchInput); }}
                placeholder="名称で絞り込み"
                className="flex-1 min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring" />
              <button type="button" onClick={() => setQ(searchInput)} aria-label="検索"
                className="min-h-[44px] min-w-[44px] px-3 rounded-md border border-surface-line hover:bg-surface-sunken focus-ring">
                <Search size={16} aria-hidden />
              </button>
            </div>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={downloadCsv} disabled={rows.length === 0}
            className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken disabled:opacity-45 focus-ring">
            <Download size={15} aria-hidden /> CSVで書き出す（{total.toLocaleString()}件）
          </button>
          {isEditable && (
            <button type="button" onClick={openCreate} disabled={!storeId}
              className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-md bg-brand-red hover:bg-brand-red-hover text-white text-[13px] font-bold btn-press-primary disabled:opacity-45 focus-ring">
              <Plus size={15} aria-hidden /> 行を追加
            </button>
          )}
          {isEditable && selected.size > 0 && (
            <button type="button" onClick={() => setBulkConfirm(true)}
              className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken focus-ring">
              <Trash2 size={15} aria-hidden /> 選択した{selected.size}件を削除
            </button>
          )}
        </div>

        {!isEditable && (
          <p className="text-xs text-text-muted">
            枠外調整はこの画面では参照のみです。追加・修正は{' '}
            <Link to="/entry/lite" className="underline font-bold">データ手入力</Link> から行ってください。
          </p>
        )}
      </section>

      {records.isLoading && <p className="text-text-muted">読み込み中…</p>}
      {records.isError && (
        <p role="alert" className="p-3 rounded-md bg-alert-bg text-alert-text border border-alert-border">
          {(records.error as Error).message}
        </p>
      )}

      {records.data && (
        <section className="border border-surface-line rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-line bg-surface-sunken flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-text-muted">
              {storeLabel} / {jpMonth(month)} — 現在の絞り込みで {total.toLocaleString()} 件
              {total > PAGE_SIZE && `（${page * PAGE_SIZE + 1}〜${Math.min((page + 1) * PAGE_SIZE, total)} 件を表示）`}
            </p>
            {pageCount > 1 && (
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                  aria-label="前のページ"
                  className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line disabled:opacity-40 hover:bg-white focus-ring flex items-center justify-center">
                  <ChevronLeft size={16} aria-hidden />
                </button>
                <span className="text-[13px] tabular-nums px-2">{page + 1} / {pageCount}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                  aria-label="次のページ"
                  className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line disabled:opacity-40 hover:bg-white focus-ring flex items-center justify-center">
                  <ChevronRight size={16} aria-hidden />
                </button>
              </div>
            )}
          </div>

          {total === 0 ? (
            <EmptyState message="該当するデータがありません" description="店舗・対象月・種別を変えてお試しください" />
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-surface-sunken text-text-muted sticky top-0 z-20">
                    <tr>
                      {isEditable && (
                        <th scope="col" className="relative sticky left-0 z-30 bg-surface-sunken px-3 py-2 w-11">
                          <label className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer"><input ref={selectAllRef} type="checkbox" checked={allSelected}
                            onChange={() => setSelected((prev) => {
                              const next = new Set(prev);
                              if (allSelected) pageIds.forEach((id) => next.delete(id));
                              else pageIds.forEach((id) => next.add(id));
                              return next;
                            })}
                            aria-label="すべての行を選択" className="w-4 h-4 align-middle" /></label>
                        </th>
                      )}
                      {columns.map((col) => (
                        <th key={col.key} scope="col"
                          className={`relative px-3 py-2 whitespace-nowrap font-bold ${col.numeric ? 'text-right' : 'text-left'}`}>
                          {col.label}
                        </th>
                      ))}
                      {isEditable && <th scope="col" className="relative sticky right-0 z-30 bg-surface-sunken px-3 py-2 text-right">操作</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.record_id} className="border-t border-surface-line hover:bg-surface-sunken/40">
                        {isEditable && (
                          <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                            <label className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer"><input type="checkbox" checked={selected.has(r.record_id)}
                              onChange={() => toggle(r.record_id)} aria-label="この行を選択" className="w-4 h-4 align-middle" /></label>
                          </td>
                        )}
                        {columns.map((col) => (
                          <td key={col.key} className={`px-3 py-1.5 ${col.numeric ? 'text-right tabular-nums' : ''}`}>
                            {cell(col.key, r)}
                          </td>
                        ))}
                        {isEditable && (
                          <td className="sticky right-0 z-10 bg-white px-2 py-1 text-right whitespace-nowrap">
                            <button type="button" aria-label="この行を修正" onClick={() => openEdit(r)}
                              className="inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-surface-sunken text-text-muted focus-ring">
                              <Pencil size={15} aria-hidden />
                            </button>
                            <button type="button" aria-label="この行を削除" onClick={() => setDeleteTarget(r)}
                              className="inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-surface-sunken text-text-muted focus-ring">
                              <Trash2 size={15} aria-hidden />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="md:hidden divide-y divide-surface-line">
                {rows.map((r) => (
                  <li key={r.record_id} className="p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      {isEditable && (
                        <label className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer"><input type="checkbox" checked={selected.has(r.record_id)} onChange={() => toggle(r.record_id)}
                          aria-label="この行を選択" className="w-5 h-5 mt-0.5 shrink-0" /></label>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[14px] break-words">{cell(columns[0]!.key, r)}</p>
                        <p className="text-[13px] text-text-muted tabular-nums">金額: {cell('amount', r)}</p>
                      </div>
                    </div>
                    {isEditable && (
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openEdit(r)}
                          className="flex-1 min-h-[44px] rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken focus-ring">修正</button>
                        <button type="button" onClick={() => setDeleteTarget(r)}
                          className="flex-1 min-h-[44px] rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken focus-ring">削除</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      <Modal
        open={!!editTarget}
        title={editTarget?.mode === 'create' ? 'データを追加' : 'データを修正'}
        size="md"
        onClose={() => setEditTarget(null)}
        footer={(
          <>
            <button type="button" onClick={() => setEditTarget(null)} disabled={saving}
              className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
              キャンセル
            </button>
            <button type="button" onClick={submit} disabled={saving || !form.target_id || form.amount === ''}
              className="h-10 px-5 rounded-md bg-brand-red hover:bg-brand-red-hover text-white text-[13px] font-extrabold btn-press-primary disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
              {saving ? '保存中…' : '保存'}
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <p className="text-[13px] text-text-muted">
            対象: <span className="font-bold text-text-body">{storeLabel} / {jpMonth(month)}</span>
          </p>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">
              {spec.targetLabel}<span className="text-brand-red ml-0.5" aria-hidden>*</span><span className="sr-only">（必須）</span>
            </span>
            <Select value={form.target_id} onValueChange={(v) => setForm((p) => ({ ...p, target_id: v }))}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選択してください" /></SelectTrigger>
              <SelectContent>
                {masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">
              金額<span className="text-brand-red ml-0.5" aria-hidden>*</span><span className="sr-only">（必須）</span>
            </span>
            <input type="number" value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
              className="w-full min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring" />
            <span className="block text-[11px] text-text-muted mt-1">返金・値引き調整はマイナスで入力します</span>
          </label>
          {modalError && (
            <p role="alert" className="rounded-md border border-alert-border bg-alert-bg px-3 py-2 text-[13px] text-alert-text">
              {modalError}
            </p>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="この行を削除しますか？"
        destructive loading={saving} confirmLabel="削除する"
        message={<p className="text-left">この操作は取り消せません。</p>}
        onConfirm={doDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirm}
        title={`選択した ${selected.size} 件を削除しますか？`}
        destructive loading={saving} confirmLabel="まとめて削除する"
        message={(
          <div className="space-y-2 text-left">
            <p>この操作は取り消せません。</p>
            <ul className="list-disc pl-4 text-[13px]">
              {rows.filter((r) => selected.has(r.record_id)).slice(0, 5).map((r) => (
                <li key={r.record_id}>{cell(columns[0]!.key, r)}</li>
              ))}
              {selected.size > 5 && <li className="list-none text-text-muted">ほか {selected.size - 5} 件</li>}
            </ul>
          </div>
        )}
        onConfirm={doBulkDelete}
        onClose={() => setBulkConfirm(false)}
      />
    </div>
  );
}
